// @flow

import produce, { original } from 'immer';
import isObjectLike from 'lodash/isObjectLike';

import { type GlobalStateType } from 'Lib/reduxType';
import isNil from 'Lib/utils/isNil';

/**
 * The reconciler resolves differences between versions of the state.
 *
 * - `inboundState` refers to the state coming from the persistence layer during rehydration.
 * - `originalState` is the previous state
 * - `reducedState` is the result of processing `originalState` with the reducer
 */

export function objectReconciler(inboundState: Object, reducedState: Object): Object {
  if (!inboundState) {
    return reducedState;
  }
  return produce(reducedState, (draft) => {
    Object.keys(inboundState).forEach((key) => {
      if (!isNil(inboundState[key]) || !(key in original(draft))) {
        const newState = inboundState[key];
        if (
          !isNil(newState) &&
          !isNil(draft[key]) &&
          isObjectLike(draft[key]) &&
          !Array.isArray(draft[key])
        ) {
          // nested merge
          draft[key] = Object.assign(draft[key], newState);
        } else {
          draft[key] = inboundState[key];
        }
      }
    });
  });
}

export default function reconciler(
  inboundState: GlobalStateType,
  reducedState: GlobalStateType
): GlobalStateType {
  return objectReconciler(inboundState, reducedState);
}
